/**
 * Кнопка «назад» закрывает то, что открыто поверх страницы, а не программу.
 *
 * Карточка заказа, окна и выдвижное меню живут в состоянии React и адреса
 * страницы не меняют. Для браузера открытие карточки — вообще не событие:
 * запись в историю не добавляется. Поэтому «назад» на телефоне уходил на шаг
 * ДО программы, то есть закрывал её целиком — с открытой карточкой, набранным
 * текстом и несохранёнными правками. Со стороны это выглядит как вылет.
 *
 * Приём: при открытии кладём в историю пустую запись (адрес не меняется —
 * `pushState` без ссылки), а «назад» ловим и закрываем накладку сами.
 *
 * Запись за собой УБИРАЕМ. Закрыли крестиком или Escape — своя запись осталась
 * бы в истории, и следующее «назад» не сделало бы ничего видимого: человек жмёт
 * второй раз, третий, а программа стоит.
 *
 * Накладки вкладываются друг в друга — окно поверх карточки, подтверждение
 * поверх окна. Каждая кладёт свою запись, и «назад» закрывает верхнюю: порядок
 * записей в истории повторяет порядок открытия.
 */

import { useEffect, useRef } from 'react';

/**
 * Сколько ближайших «назад» — наши собственные, а не нажатые человеком.
 *
 * Убирая свою запись, мы вызываем `history.back()`, и браузер отвечает тем же
 * событием `popstate`, что и настоящая кнопка «назад». Отличить их иначе
 * нельзя, а спутать — дорого: в разработке React намеренно прогоняет каждый
 * такой приём дважды (открыл — закрыл — открыл снова), и наш же шаг назад
 * прилетал во второй заход как чужой. Карточка заказа открывалась и тут же
 * захлопывалась, и открыть её было невозможно вовсе.
 *
 * Счётчик общий на всю программу: накладки вкладываются, и каждая может
 * оставить свой шаг назад в очереди.
 */
let своихШагов = 0;

export function useCloseOnBack(active: boolean, onClose: () => void): void {
  /*
   * Обработчик держим в ссылке, а не в зависимостях.
   *
   * Экраны передают сюда стрелочную функцию, которая создаётся заново на каждой
   * отрисовке. Попади она в зависимости — при первой же перерисовке (а их за
   * время открытого окна десятки: ввод в поле, приход данных) приём запускался
   * бы заново и клал в историю ещё одну запись. Дальше «назад» пришлось бы
   * нажимать столько раз, сколько букв набрано.
   */
  const закрыть = useRef(onClose);
  закрыть.current = onClose;

  useEffect(() => {
    if (!active) return;

    window.history.pushState({ otcOverlay: true }, '');

    let закрытоНазадом = false;
    const наНазад = () => {
      if (своихШагов > 0) {
        своихШагов -= 1;
        return;
      }
      закрытоНазадом = true;
      закрыть.current();
    };
    window.addEventListener('popstate', наНазад);

    return () => {
      window.removeEventListener('popstate', наНазад);

      /*
       * Свою запись снимаем, ТОЛЬКО ЕСЛИ она всё ещё верхняя.
       *
       * Накладка закрывается и переходом в другой раздел: в меню на телефоне
       * нажатие пункта одновременно уводит на новый адрес и закрывает меню.
       * Переход кладёт поверх нашей записи свою, и шаг назад отменял бы уже
       * не наше — а именно этот переход. Раздел не переключался: программа
       * послушно возвращалась туда, откуда её только что увели.
       *
       * Признак свой записи — метка `otcOverlay`. После перехода наверху лежит
       * запись маршрутизатора, метки на ней нет, и мы ничего не трогаем: наша
       * запись остаётся ниже, и «назад» из нового раздела вернёт в прежний,
       * как и ожидается.
       */
      const наверхуНаша = (window.history.state as { otcOverlay?: boolean } | null)?.otcOverlay;
      if (!закрытоНазадом && наверхуНаша) {
        своихШагов += 1;
        window.history.back();
      }
    };
  }, [active]);
}
